import type { AppAudience } from "@amsaas/shared";

/**
 * Host-as-trust-boundary helpers (TOP-03, AUTH-06/08, GAP-001/021/025).
 *
 * One `apps/web` serves two hosts, selected at runtime by hostname:
 *   amcaas* → B2C shell · everything else (amsaas*, bare localhost) → B2B shell.
 * Dev simulates the two hosts via two loopback hostnames (amsaas.localhost /
 * amcaas.localhost) — NOT two ports (ports share cookies; see GAP-021).
 */
export type AppHost = "b2b" | "b2c";

const OVERRIDE_KEY = "amsaas:appHost";

// Dev override (mirrors dispatch.tsx's isAmcaasHost): `?app=amcaas` / `?app=amsaas`
// simulates either host on plain `localhost`, since Clerk dev instances only trust
// localhost (GAP-021). Captured ONCE at module load — before any navigation or the
// Clerk sign-in redirect drops the query param — and persisted for the tab session.
// Inert in prod (real hosts pass neither). To switch/clear: reload with the opposite
// `?app=`, or clear sessionStorage.
try {
  const o = new URLSearchParams(window.location.search).get("app");
  if (o === "amcaas") sessionStorage.setItem(OVERRIDE_KEY, "b2c");
  else if (o === "amsaas") sessionStorage.setItem(OVERRIDE_KEY, "b2b");
} catch {
  /* sessionStorage unavailable */
}

export function getAppHost(): AppHost {
  try {
    const stored = sessionStorage.getItem(OVERRIDE_KEY);
    if (stored === "b2c" || stored === "b2b") return stored;
  } catch {
    /* sessionStorage unavailable */
  }
  return window.location.hostname.startsWith("amcaas") ? "b2c" : "b2b";
}

/**
 * Whether the app host is **explicitly** determined: a real `amcaas*`/`amsaas*`
 * hostname (prod), or a dev `?app=` override (persisted). A bare `localhost` with no
 * override is ambiguous — login is gated until the operator picks an app, so the wrong
 * door is never shown by accident in dev.
 */
export function isAppHostResolved(): boolean {
  try {
    const stored = sessionStorage.getItem(OVERRIDE_KEY);
    if (stored === "b2c" || stored === "b2b") return true;
  } catch {
    /* sessionStorage unavailable */
  }
  const h = window.location.hostname;
  return h.startsWith("amcaas") || h.startsWith("amsaas");
}

/** True if an identity with this audience is allowed on the given host. */
export function audienceAllowedOnHost(
  audience: AppAudience,
  host: AppHost,
): boolean {
  if (audience === "both") return true;
  return audience === host;
}

/**
 * URL of the counterpart app (for "use the other app" links), preserving
 * scheme + port. On a bare `localhost` dev host (no amsaas/amcaas prefix) there
 * is no counterpart, so this returns null.
 */
export function otherAppUrl(path = "/"): string | null {
  const { protocol, hostname, port } = window.location;
  let swapped: string | null = null;
  if (hostname.startsWith("amcaas"))
    swapped = hostname.replace(/^amcaas/, "amsaas");
  else if (hostname.startsWith("amsaas"))
    swapped = hostname.replace(/^amsaas/, "amcaas");
  if (!swapped) return null;
  return `${protocol}//${swapped}${port ? `:${port}` : ""}${path}`;
}
